<template>
  <v-container fluid fill-height data-testid="signup_page_sign_up">
    <v-layout align-center justify-center>
      <v-flex id="opticalgym_signup_page_signup" sm8>
        <div v-if="load_error">
          <v-alert type="error" outlined data-testid="signup_page_sign_up_not_found">
            {{ $t("signup_page_sign_up.page_not_found") }}
          </v-alert>
        </div>
        <div v-else-if="!page">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="!existing_user">
          <h1 v-if="page.title" class="mb-4" data-testid="signup_page_sign_up_title">{{ page.title }}</h1>
          <p v-if="page.instructions" class="mb-6" data-testid="signup_page_sign_up_instructions">{{ page.instructions }}</p>
          <table class="clinic_sign_up">
            <tr>
              <td>{{ $t("clinic_sign_up.business_name") }}</td>
              <td>
                <v-text-field v-model="clinic.title" type="text" dense data-testid="signup_page_sign_up_business_name" />
              </td>
            </tr>

            <!-- space -->

            <tr>
              <td>{{ $t("clinic_sign_up.your_name") }}</td>
              <td>
                <v-layout justify-space-around>
                  <v-flex xs2>
                    <v-text-field
                      ref="name_prefix"
                      v-model="user.name_prefix"
                      :label="$t('common.title')"
                      type="text"
                      dense
                      data-testid="signup_page_sign_up_name_prefix"
                    />
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      ref="name_given"
                      v-model="user.name_given"
                      :label="$t('common.first_name')"
                      type="text"
                      dense
                      data-testid="signup_page_sign_up_name_given"
                    />
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      ref="name_family"
                      v-model="user.name_family"
                      :label="$t('common.last_name')"
                      type="text"
                      dense
                      data-testid="signup_page_sign_up_name_family"
                    />
                  </v-flex>
                  <v-flex xs2>
                    <v-text-field
                      ref="name_suffix"
                      v-model="user.name_credentials"
                      :label="$t('common.suffix')"
                      type="text"
                      dense
                      data-testid="signup_page_sign_up_name_suffix"
                    />
                  </v-flex>
                </v-layout>
              </td>
            </tr>

            <tr>
              <td>{{ $t("clinic_sign_up.your_email") }}</td>
              <td>
                <EmailField
                  v-model="user.email"
                  label
                  :backend_checking="false"
                  :outlined="false"
                  @valid-email="update_valid_email"
                  dense
                />
              </td>
            </tr>

            <tr>
              <td valign="top">
                <div style="padding-top: 15px">{{ $t("common.choose_a_password") }}</div>
              </td>
              <td>
                <DoublePassword
                  v-model="user.pass"
                  load-on-mount
                  always-show-confirm
                  @passwords-match="update_password_match"
                  inline
                  class="ml-3 mr-3"
                />
              </td>
            </tr>

            <br />

            <tr>
              <td>{{ $t("common.country") }}</td>
              <td>
                <v-select
                  v-model="clinic.billing_country"
                  :items="country_options"
                  dense
                  data-testid="signup_page_sign_up_country"
                />
              </td>
            </tr>

            <tr v-if="clinic.billing_country == '__'">
              <td>{{ $t('clinic_sign_up.other_country') }}</td>
              <td style="font-size: smaller">
                {{ $t('clinic_signup.country_not_supported') }}
                <v-select v-model="unsupported_country" :items="extra_countries"> dense</v-select>
              </td>
            </tr>

            <tr>
              <td>{{ $t("common.city") }} <span v-if="district_enabled"> / {{ district_name }}</span></td>
              <td>

                <v-layout justify-space-around>
                  <v-flex :xs5="district_enabled">
                    <v-text-field
                      v-model="clinic.physical_locality"
                      :label="$t('common.city')"
                      dense
                      data-testid="signup_page_sign_up_city"
                    />
                  </v-flex>
                  <v-flex xs1 v-if="district_enabled" />
                  <v-flex xs6 v-if="district_enabled">
                    <v-select
                      v-model="clinic.billing_district"
                      :items="district_options"
                      :label="district_name"
                      dense
                      data-testid="signup_page_sign_up_district"
                    />
                  </v-flex>
                </v-layout>
                
              </td>
            </tr>

            <tr v-if="collect_affiliation">
              <td>{{ $t("clinic_sign_up.affiliation") }}</td>
              <td>
                <v-autocomplete
                  v-model="user.affiliation"
                  :items="affiliations"
                  item-text="text"
                  item-value="value"
                  :hint="$t('clinic_sign_up.affiliation_hint')"
                  persistent-hint
                  dense
                  data-testid="signup_page_sign_up_affiliation"
                />
              </td>
            </tr>

            <tr>
              <td valign="top">
                <div style="padding-top: 15px">
                  {{ $t("clinic_sign_up.coupon_code") }}
                  <br />
                  <span style="font-size: 0.7em; color: grey">{{ $t('common.optional') }}</span>
                </div>
              </td>
              <td>
                <v-flex xs8>
                  <v-text-field
                    v-model="coupon_code"
                    type="text"
                    dense
                    :hint="$t('clinic_sign_up.coupon_code_hint')"
                    persistent-hint
                    data-testid="signup_page_sign_up_coupon"
                  />
                </v-flex>
              </td>
            </tr>

            <br />

            <tr v-if="collect_notes">
              <td valign="top">
                <div>
                  {{ $t("clinic_sign_up.notes") }}
                  <br />
                  <span style="font-size: 0.7em; color: grey">{{ $t('common.optional') }}</span>
                </div>
              </td>
              <td>
                <v-flex xs12>
                  <v-textarea
                    v-model="notes"
                    :placeholder="$t('clinic_sign_up.notes_placeholder')"
                    rows="2"
                    auto-grow
                    dense
                  />
                </v-flex>
              </td>
            </tr>
          </table>

          <div v-if="has_survey" class="mt-6 mb-4" data-testid="signup_page_sign_up_survey">
            <VuetifySurvey ref="survey" v-model="survey_answers" :survey="page.survey" />
          </div>

          <v-checkbox
            v-if="require_attestation"
            v-model="certify"
            style="margin-top: 10px"
            data-testid="signup_page_sign_up_certify"
          >
            <template v-slot:label>
              <span style="font-size: 0.9em;">
                <span @click.stop v-html="$t('clinic_sign_up.agree_to_terms')"></span>
                <span>{{ $t("clinic_sign_up.certify_that_am_an_optometrist") }}</span>
              </span>
            </template>
          </v-checkbox>

          <v-checkbox
            v-if="collect_marketing"
            v-model="marketing"
            style="margin-top: -10px"
            data-testid="signup_page_sign_up_marketing"
          >
            <template v-slot:label>
              <span style="font-size: 0.9em;">{{ $t("clinic_sign_up.agree_marketing") }}</span>
            </template>
          </v-checkbox>

          <p
            style="font-size: small; color: grey; margin-top: 5px; margin-bottom:5px; text-align:right;"
            :html="$t('clinic_sign_up.by_signing_up', { terms_of_use: '<a href=\'/terms\' target=\'_blank\'>' + $t('common.terms_of_use') + '</a>'}) ">
          </p>

          <v-layout>
            <v-flex sm2>
              <v-btn
                large
                color="primary"
                :loading="in_flight"
                :disabled="in_flight"
                @click="submit"
                data-testid="signup_page_sign_up_sign_up"
              >
                  {{ $t("clinic_sign_up.sign_up") }}
              </v-btn>
            </v-flex>
            <v-flex sm10>
              <v-alert v-if="errors.length > 0" type="error" outlined data-testid="signup_page_sign_up_errors">
                <div v-for="(err, index) in errors" :key="index">{{ err }}</div>
              </v-alert>
            </v-flex>
          </v-layout>
        </div>
        <div v-else>
          <div v-if="existing_user" data-testid="signup_page_sign_up_existing_user">
            <p>{{ $t("clinic_sign_up.already_registered_on_neurovisual_trainer", { email: user.email}) }}</p>
            <p>{{ $t("clinic_sign_up.to_log_into_your_existing_account") }}</p>
            <p>{{ $t("clinic_sign_up.to_sign_up_a_new_business_and_start_free_trial") }}</p>
          </div>
          <ContactInfo style="margin-top: 50px; margin-left: 120px" :include_address="false" />
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import axios from "axios";
import EmailField from "./EmailField.vue";
import DoublePassword from "./DoublePassword.vue";
import ContactInfo from "./ContactInfo.vue";
import VuetifySurvey from "./vuetify-survey/components/VuetifySurvey.vue";
import all_countries from "../../../data/all_countries_list.json";
import locales from "../../../data/locales.yml";
import assocs from "../../../data/associations.yml";
import { mapState } from "vuex";
import Clinic from "../classes/clinic";
import User from "../classes/user";

function signupPageSearchParams(routeQuery) {
  let search = "";
  try {
    search = window.top.location.search;
  } catch (e) {
    search = window.location.search;
  }
  const params = new URLSearchParams(search);
  if (routeQuery) {
    for (const [key, value] of Object.entries(routeQuery)) {
      if (value == null || value === "") {
        continue;
      }
      if (Array.isArray(value)) {
        params.delete(key);
        for (const item of value) {
          if (item) {
            params.append(key, String(item));
          }
        }
      } else if (!params.has(key)) {
        params.set(key, String(value));
      }
    }
  }
  return params;
}

function queryValue(params, key) {
  const value = params.get(key);
  return value && String(value).trim() ? String(value) : "";
}

function surveyAnswersFromQuery(params) {
  const survey_answers = {};
  for (const [key, value] of params.entries()) {
    if (!key.startsWith("survey.") || !value) {
      continue;
    }
    const id = key.slice("survey.".length);
    if (!id) {
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(survey_answers, id)) {
      const current = survey_answers[id];
      survey_answers[id] = Array.isArray(current) ? current.concat([value]) : [current, value];
    } else {
      survey_answers[id] = value;
    }
  }
  return survey_answers;
}

export default {
  components: { EmailField, DoublePassword, ContactInfo, VuetifySurvey },
  props: {
    collect_affiliation: {
      type: Boolean,
      default: () => true,
    },
    collect_notes: {
      type: Boolean,
      default: () => true,
    },
    collect_marketing: {
      type: Boolean,
      default: () => false,
    },
    require_attestation: {
      type: Boolean,
      default: () => true,
    },
  },
  data() {
    const query = signupPageSearchParams(this.$route && this.$route.query);
    const tag = queryValue(query, "tag");
    const tags = tag ? tag.split(",") : [];
    const affiliation = queryValue(query, "affiliation");
    const country_from_query = queryValue(query, "country") || queryValue(query, "billing_country");
    const default_country = country_from_query || "US";
    const display_error = queryValue(query, "display_error");
    const error_code = queryValue(query, "error");

    // Country options
    let country_options = [];
    for (const [_key, locale] of Object.entries(locales)) {
      if (!locale.hidden) {
        country_options.push({
          text: locale.name,
          value: locale.code,
        });
      }
    }
    country_options.sort((a, b) => a.text.localeCompare(b.text));

    country_options.push({
      text: "Other",
      value: "__",
    });

    let extra_countries = [];
    for (var country of all_countries) {
      if (!locales[country.value] || (locales[country.value] && locales[country.value].hidden)) {
        extra_countries.push(country);
      }
    }

    let data = {
      certify: false,
      terms: false,
      marketing: queryValue(query, "marketing_emails") === "true" || queryValue(query, "marketing_emails") === "on" || queryValue(query, "marketing_emails") === "1",
      errors: display_error ? [display_error] : [],
      locales,
      country_options,
      extra_countries,
      ip_country: null,
      passwords_match: true,
      valid_email: true,
      in_flight: false,
      existing_user: error_code === "InviteUserExists",
      show_visible_capcha: false,
      coupon_code: queryValue(query, "coupon"),
      unsupported_country: "AF",
      blocked_countries: ["RU"],
      notes: queryValue(query, "notes"),
      page: null,
      load_error: false,
      survey_answers: surveyAnswersFromQuery(query),
      country_from_query: !!country_from_query,
      clinic: Clinic.fromJson({
        title: queryValue(query, "title"),
        phone: queryValue(query, "phone"),
        website: queryValue(query, "website"),
        physical_address_1: queryValue(query, "physical_address_1"),
        physical_address_2: queryValue(query, "physical_address_2"),
        physical_locality: queryValue(query, "physical_locality"),
        physical_district: queryValue(query, "physical_district"),
        physical_postal: queryValue(query, "physical_postal"),
        billing_district: queryValue(query, "billing_district"),
        billing_country: default_country,
        status: "pending",
        tags: tags,
        app: window.app_config.id,
        billing_strategy: window.app_config.default_billing_strategy,
      }),
      user: User.fromJson({
        name_prefix: queryValue(query, "name_prefix"),
        name_given: queryValue(query, "name_given"),
        name_family: queryValue(query, "name_family"),
        name_credentials: queryValue(query, "name_credentials"),
        email: queryValue(query, "email"),
        pass: "",
        affiliation,
        tags: [],
      }),
    };

    return data;
  },
  computed: {
    ...mapState(["app_config"]),
    has_survey() {
      return this.page && this.page.survey && this.page.survey.items && this.page.survey.items.length > 0;
    },
    affiliations() {
      const lang = this.$i18n.lang();
      let userCountry = "US";
      if (this.ip_country && this.ip_country.code && this.ip_country.code != "XX") {
        userCountry = this.ip_country.code;
      }

      const entries = Object.entries(assocs).map(([key, data]) => {
        // Resolve label based on language or fallback
        let label = data.title[lang];
        if (!label) {
          // Use the first available language
          const firstLang = Object.keys(data.title)[0];
          label = data.title[firstLang];
        }
        return {
          key,
          label,
          countries: data.countries || []
        };
      });

      // Sort alphabetically by key (name)
      entries.sort((a, b) => a.key.localeCompare(b.key));

      // Now prioritize:
      // 1. Associations in user's country
      // 2. Associations with no countries (international)
      // 3. All others
      const inUserCountry = [];
      const international = [];
      const others = [];

      for (const entry of entries) {
        if (entry.countries.length === 0) {
          international.push(entry);
        } else if (entry.countries.includes(userCountry)) {
          inUserCountry.push(entry);
        } else {
          others.push(entry);
        }
      }

      const sorted = [...inUserCountry, ...international, ...others];

      let all_sorted = sorted.map(entry => ({
        text: entry.label,
        value: entry.key,
      }));

      // Add "Other" option at the end
      all_sorted.push({
        text: this.$t("clinic_sign_up.other"),
        value: "OTHER",
      });

      return all_sorted;
    },
    district_enabled() {
      if (this.locales[this.clinic.billing_country]) {
        if (this.locales[this.clinic.billing_country].districts) {
          return true;
        }
      }
      return false;
    },
    district_name() {
      if (this.district_enabled) {
        return this.locales[this.clinic.billing_country].district_name;
      }
      return "";
    },
    district_options() {
      let options = [];
      if (this.district_enabled) {
        // District is key => object
        for (const [key, dist] of Object.entries(this.locales[this.clinic.billing_country].districts)) {
          options.push({
            text: dist.name,
            value: key,
          });
        }
      }
      return options;
    }
  },
  mounted() {
    axios.get("/api/locale/ip_country").then((response) => {
      this.ip_country = response.data.data;

      if (this.locale) {
        this.assign_default_country();
      }
    });
    this.load_page();
  },
  methods: {
    load_page() {
      const signup_id = this.$route.params.signup_id;
      axios.get("/api/signup_page/" + signup_id + "/public").then((response) => {
        if (response.data.status == "error") {
          this.load_error = true;
          return;
        }
        this.page = response.data.data;
      }).catch(() => {
        this.load_error = true;
      });
    },
    assign_default_country() {
      if (this.country_from_query) {
        return;
      }
      let locale = this.locales[this.ip_country.code];
      if (locale && !locale.hidden) {
        this.clinic.billing_country = locale.code;
        if (locale.default_association) {
          this.user.affiliation = locale.default_association;
        }
      }
    },
    submit() {
      let ok = this.check();
      if (!ok) {
        return;
      }

      let clinic = this.clinic;
      if (clinic.billing_country == "__") {
        clinic.billing_country = this.unsupported_country;
      }

      if (this.notes) {
        clinic.notes = "User Signup Notes: " + this.notes;
      }

      this.in_flight = true;
      let clinic_payload = {
        title: clinic.title,
        phone: clinic.phone,
        website: clinic.website,
        physical_address_1: clinic.physical_address_1,
        physical_address_2: clinic.physical_address_2,
        physical_sub_locality: clinic.physical_sub_locality,
        physical_locality: clinic.physical_locality,
        physical_sub_district: clinic.physical_sub_district,
        physical_district: clinic.physical_district,
        physical_postal: clinic.physical_postal,
        billing_district: clinic.billing_district,
        billing_country: clinic.billing_country,
        notes: clinic.notes,
      };
      if (this.app_config.allow_clinic_signup_tags) {
        clinic_payload.tags = clinic.tags;
      }
      let payload = {
        clinic: clinic_payload,
        user: {
          email: this.user.email,
          name_prefix: this.user.name_prefix,
          name_given: this.user.name_given,
          name_family: this.user.name_family,
          name_credentials: this.user.name_credentials,
          affiliation: this.user.affiliation,
          marketing_emails: this.marketing,
        },
        pass: this.user.pass,
        certify: this.certify,
        accept_terms: this.certify,
        survey_answers: this.survey_answers,
      };

      if (this.coupon_code) {
        payload.coupon = this.coupon_code.trim();
      }

      axios.post("/api/signup/" + this.$route.params.signup_id, payload).then((response) => {
        this.in_flight = false;
        let resp = response.data;

        this.existing_user = false;

        if (resp.status == "error") {
          if (resp.error_code == "InviteUserExists") {
            this.existing_user = true;
          } else if (resp.error_code == "InvalidCouponCode") {
            this.errors.push("The provided sign-up code is invalid. Please double-check it and try again.");
            return;
          } else {
            if (typeof resp.error_code == "object") {
              if (resp.error_code["EmailHighRisk"]) {
                this.errors.push("Invalid Email, please check that the email provided is valid and try again.");
                return;
              } else {
                messageBus.$emit("api-error", resp);
              }
            } else {
              messageBus.$emit("api-error", resp);
            }
          }
        } else {
          window.location.href = "/app/clinic/" + resp.data + "?new=true";
        }
      });
    },

    check() {
      var errors = [];

      if (!this.clinic.title) {
        errors.push(this.$t("clinic_sign_up.please_provide_a_business_name"));
      }
      if (!this.user.name_given && !this.user.name_family) {
        errors.push(this.$t("clinic_sign_up.please_provide_your_name"));
      } else if (!this.user.name_given) {
        errors.push(this.$t("clinic_sign_up.please_provide_your_first_name"));
      } else if (!this.user.name_family) {
        errors.push(this.$t("clinic_sign_up.please_provide_your_last_name"));
      }
      if (!this.user.email) {
        errors.push(this.$t("clinic_sign_up.please_provide_an_email_address"));
      }
      if (this.user.email && !this.valid_email) {
        errors.push(this.$t("clinic_sign_up.there_was_a_problem_with_your_email_address"));
      }
      if (!this.clinic.title) {
        errors.push(this.$t("clinic_sign_up.please_provide_a_business_name"));
      }

      if (!this.user.affiliation && this.collect_affiliation) {
        errors.push(this.$t("clinic_sign_up.please_select_the_professional_body"));
      }

      if (!this.certify && this.require_attestation) {
        errors.push(this.$t("clinic_sign_up.please_certify_you_are_vision_professional"));
      }
      if (this.user.pass && !this.passwords_match) {
        errors.push(this.$t("clinic_sign_up.passwords_do_not_match"));
      }

      if (this.blocked_countries.includes(this.clinic.billing_country)) {
        errors.push(this.$t("clinic_sign_up.blocked_country"));
      }

      if (this.has_survey && this.$refs.survey && !this.$refs.survey.validate()) {
        errors.push(this.$refs.survey.error_message || this.$t("signup_page_sign_up.please_complete_custom_fields"));
      }

      this.errors = errors;
      return errors.length == 0;
    },

    update_password_match(val) {
      this.passwords_match = val;
    },
    update_valid_email(val) {
      this.valid_email = val;
    },
  },
};
</script>

<style>
table.clinic_sign_up tr > td:first-child {
  width: 200px;
}
</style>
